import { apiClient } from '@/shared/lib/apiClient';
import type { ApiResponse, PaginatedResponse } from '@/types';
import type { BlogPost, CreateBlogPayload, UpdateBlogPayload } from '../types';

export const blogKeys = {
  all: ['blogs'] as const,
  list: (params?: Record<string, unknown>) => [...blogKeys.all, 'list', params] as const,
  detail: (slug: string) => [...blogKeys.all, 'detail', slug] as const,
  admin: {
    all: ['admin-blogs'] as const,
    list: (params?: Record<string, unknown>) => [...blogKeys.admin.all, 'list', params] as const,
    detail: (id: string) => [...blogKeys.admin.all, 'detail', id] as const,
  },
};

export const blogApi = {
  async listPublished(params?: { page?: number; limit?: number }) {
    const response = await apiClient.get<PaginatedResponse<BlogPost>>('/blogs', { params });
    return response.data;
  },

  async getBySlug(slug: string) {
    const response = await apiClient.get<ApiResponse<BlogPost>>(`/blogs/${slug}`);
    return response.data.data;
  },

  async listAdmin(params?: { page?: number; limit?: number; published?: string }) {
    const response = await apiClient.get<PaginatedResponse<BlogPost>>('/admin/blogs', { params });
    return response.data;
  },

  async getAdminById(id: string) {
    const response = await apiClient.get<ApiResponse<BlogPost>>(`/admin/blogs/${id}`);
    return response.data.data;
  },

  async create(payload: CreateBlogPayload) {
    const response = await apiClient.post<ApiResponse<BlogPost>>('/admin/blogs', payload);
    return response.data.data;
  },

  async update(id: string, payload: UpdateBlogPayload) {
    const response = await apiClient.patch<ApiResponse<BlogPost>>(`/admin/blogs/${id}`, payload);
    return response.data.data;
  },

  async remove(id: string) {
    const response = await apiClient.delete<ApiResponse<{ id: string }>>(`/admin/blogs/${id}`);
    return response.data.data;
  },

  async uploadImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<ApiResponse<{ url: string; publicId: string }>>(
      '/upload/blog-image',
      formData
    );
    return response.data.data;
  },
};
